<template>
  <div class="ai-availability-page">
    <!-- 页面标题 -->
    <div class="page-title">
      <span class="title-bar"></span>{{ $t('ai_availability.title') }}
      <Button type="primary" size="small" class="title-refresh" :loading="refreshing" @click="refreshAll">{{ $t('ai_availability.refreshNow') }}</Button>
    </div>

    <!-- 顶部综合面板：健康状态 + 今日核心指标 -->
    <div class="overview-panel">
      <Row type="flex">
        <!-- 健康状态 FR-002 -->
        <Col class="overview-col health-col" :span="8">
          <div class="health-main">
            <span class="health-dot-lg" :style="{ backgroundColor: healthColor }"></span>
            <div class="health-info">
              <div class="health-name">AgentVerse <span :style="{ color: healthColor }">{{ healthText }}</span></div>
              <div class="health-sub">{{ $t('ai_availability.healthSubtitle') }}</div>
            </div>
            <div class="health-metrics">
              <div class="health-metric">
                <div class="hm-label">{{ $t('ai_availability.responseDelay') }}</div>
                <div class="hm-value">{{ health.latencyMs != null ? health.latencyMs + 'ms' : '-' }}</div>
              </div>
              <div class="health-metric">
                <div class="hm-label">{{ $t('ai_availability.consecutiveFailures') }}</div>
                <div class="hm-value">{{ health.continuousFailCount != null ? health.continuousFailCount + $t('ai_availability.timesUnit') : '-' }}</div>
              </div>
            </div>
          </div>
          <div class="probe-row">
            <span class="probe-label">{{ $t('ai_availability.recentProbes') }}</span>
            <span class="probe-dots">
              <template v-if="recentProbes.length">
                <Tooltip v-for="(probe, idx) in recentProbes" :key="idx" :transfer="true" placement="top">
                  <span class="probe-dot" :style="{ backgroundColor: probeColor(probe) }"></span>
                  <div slot="content">
                    <p>{{ $t('ai_availability.probeTime') }}：{{ probe.probeTime || '-' }}</p>
                    <p>{{ $t('ai_availability.probeStatus') }}：{{ probeStatusText(probe) }}</p>
                    <p>{{ $t('ai_availability.probeDelay') }}：{{ probe.latencyMs != null ? probe.latencyMs + 'ms' : '-' }}</p>
                  </div>
                </Tooltip>
              </template>
              <span v-else class="probe-empty">-</span>
            </span>
            <span class="probe-legend">
              <span class="legend-item"><span class="probe-dot legend-dot" style="background:#19be6b;"></span>{{ $t('ai_availability.probeSuccess') }}</span>
              <span class="legend-item"><span class="probe-dot legend-dot" style="background:#ff9900;"></span>{{ $t('ai_availability.probeSlow') }}</span>
              <span class="legend-item"><span class="probe-dot legend-dot" style="background:#ed4014;"></span>{{ $t('ai_availability.probeFail') }}</span>
            </span>
          </div>
        </Col>

        <!-- 今日核心指标 FR-003 -->
        <Col class="overview-col metric-col metric-total" :span="4">
          <div class="metric-label">{{ $t('ai_availability.todayAlertTotal') }}</div>
          <div class="metric-value">{{ metrics.todayAlarmTotal }}</div>
          <div class="metric-desc">{{ $t('ai_availability.todayAlertTotalDesc') }}</div>
        </Col>
        <Col class="overview-col metric-col metric-processed" :span="4">
          <div class="metric-label">{{ $t('ai_availability.aiProcessedCount') }}</div>
          <div class="metric-value">{{ metrics.aiProcessedCount }}</div>
          <div class="metric-desc">{{ $t('ai_availability.aiProcessedCountDesc') }}</div>
        </Col>
        <Col class="overview-col metric-col metric-timeout" :span="4">
          <div class="metric-label">{{ $t('ai_availability.todayTimeoutNotifyCount') }}</div>
          <div class="metric-value">{{ metrics.todayTimeoutNoticeCount }}</div>
          <div class="metric-desc">{{ $t('ai_availability.todayTimeoutNotifyCountDesc') }}</div>
        </Col>
        <Col class="overview-col metric-col metric-fault" :span="4">
          <div class="metric-label">{{ $t('ai_availability.currentFaultCount') }}</div>
          <div class="metric-value">{{ metrics.currentFaultCount }}</div>
          <div class="metric-desc">{{ $t('ai_availability.currentFaultCountDesc') }}</div>
        </Col>
      </Row>
    </div>

    <!-- 当前故障工单列表 FR-004 -->
    <div class="fault-panel">
      <div class="panel-header">
        <span class="panel-title"><span class="title-bar"></span>{{ $t('ai_availability.faultListTitle') }}</span>
        <span class="refresh-time">{{ $t('ai_availability.lastRefreshTime') }}：{{ lastRefreshTime || '-' }}</span>
      </div>

      <!-- 搜索区 -->
      <Row :gutter="8" class="search-row">
        <Col span="5" v-if="companyIdCookie==1">
          <Input v-model="searchForm.companyName" clearable :placeholder="$t('ai_availability.companyPlaceholder')"></Input>
        </Col>
        <Col span="5">
          <Input v-model="searchForm.ticketNumber" clearable :placeholder="$t('ai_availability.ticketPlaceholder')"></Input>
        </Col>
        <Col span="5">
          <Select v-model="searchForm.priority" clearable :placeholder="$t('ai_availability.priorityPlaceholder')" :transfer="true">
            <Option v-for="item in priorityOptions" :value="item.value" :key="item.value">{{ item.label }}</Option>
          </Select>
        </Col>
        <Col span="5">
          <DatePicker
            type="datetimerange"
            format="yyyy-MM-dd HH:mm:ss"
            v-model="searchForm.alarmTimeRange"
            :placeholder="$t('ai_availability.alarmTimePlaceholder')"
            :transfer="true"
            @on-change="onAlarmTimeChange"
            style="width: 100%;">
          </DatePicker>
        </Col>
        <Col span="4">
          <Button type="primary" @click="search">{{ $t('ai_availability.searchBtn') }}</Button>
          <Button style="margin-left: 8px;" @click="resetSearch">{{ $t('ai_availability.resetBtn') }}</Button>
        </Col>
      </Row>

      <Table
        :no-data-text="$t('common.noData')"
        :columns="columns"
        :data="faultTickets"
        :loading="tableLoading"
        :row-class-name="rowClassName"
        style="width: 100%;">
        <template slot-scope="{ row }" slot="ticketNumber">
          <a style="color:#2d8cf0;" @click="toTicketDetail(row)">{{ row.ticketNumber || '-' }}</a>
        </template>
        <template slot-scope="{ row }" slot="priority">
          <Tag :color="priorityColor(row.priorityIndex)">{{ row.priority || '-' }}</Tag>
        </template>
        <template slot-scope="{ row }" slot="faultType">
          <span>{{ faultTypeText(row) }}</span>
        </template>
        <template slot-scope="{ row }" slot="stuckDuration">
          <span class="stuck-duration">{{ stuckDurationText(row) }}</span>
        </template>
        <template slot-scope="{ row }" slot="status">
          <Tag :color="ticketStatusColor(row.status)">{{ row.statusText || ticketStatusText(row.status) }}</Tag>
        </template>
        <template slot-scope="{ row }" slot="notified">
          <Tag v-if="row.noticed" color="orange">{{ $t('ai_availability.notified') }}</Tag>
          <span v-else>-</span>
        </template>
        <template slot-scope="{ row }" slot="operation">
          <Button type="primary" size="small" @click="toTicketDetail(row)">{{ $t('ai_availability.ticketDetail') }}</Button>
        </template>
      </Table>

      <Page
        :current="current"
        :total="total"
        show-sizer
        :page-size="pageSize"
        @on-change="changePage"
        @on-page-size-change="changeSize"
        :transfer="true"
        class="page-bar">
      </Page>
    </div>
  </div>
</template>

<script>
import { getCookie } from '../../../utils/cookieUtil.js';

// 健康状态枚举（业务口径 5.3）
var HEALTH_ONLINE = 'ONLINE';
var HEALTH_SLOW = 'SLOW';
var HEALTH_OFFLINE = 'OFFLINE';
var HEALTH_UNKNOWN = 'UNKNOWN';

// 工单状态枚举（仅列表会展示的状态作为 statusText 兜底：40 人工通知 / 50 人工介入）
var TICKET_STATUS = {
  40: { textKey: 'statusManualNotify', color: 'red' },
  50: { textKey: 'statusManualIntervene', color: 'gold' }
};

// 后端返回的 color 字段 -> 十六进制色值
var COLOR_MAP = {
  green: '#19be6b',
  orange: '#ff9900',
  red: '#ed4014',
  gray: '#c5c8ce'
};

// 自动刷新频率：15 秒（FR-005 / AC-011）
var REFRESH_INTERVAL = 15 * 1000;

export default {
  name: 'AIAvailabilityDashboard',
  data() {
    return {
      companyIdCookie: getCookie(global.companyId),
      refreshing: false,
      tableLoading: false,
      lastRefreshTime: '',
      timer: null,
      // 刷新进行中标记，避免自动刷新重复堆叠
      refreshInFlight: false,
      health: {
        status: HEALTH_UNKNOWN,
        statusText: '',
        color: 'gray',
        latencyMs: null,
        continuousFailCount: null
      },
      recentProbes: [],
      metrics: {
        todayAlarmTotal: 0,
        aiProcessedCount: 0,
        todayTimeoutNoticeCount: 0,
        currentFaultCount: 0
      },
      searchForm: {
        companyName: '',
        ticketNumber: '',
        priority: '',
        alarmTimeRange: ['', ''],
        alarmTimeStart: '',
        alarmTimeEnd: ''
      },
      current: 1,
      pageSize: 10,
      total: 0,
      faultTickets: [],
      // 用于高亮新出现的故障工单
      knownTicketKeys: []
    };
  },
  computed: {
    healthText() {
      // 优先使用后端 statusText，缺省时按状态码兜底
      if (this.health.statusText) return this.health.statusText;
      switch (this.health.status) {
        case HEALTH_ONLINE: return this.$t('ai_availability.online');
        case HEALTH_SLOW: return this.$t('ai_availability.slow');
        case HEALTH_OFFLINE: return this.$t('ai_availability.offline');
        default: return this.$t('ai_availability.unknown');
      }
    },
    healthColor() {
      // 颜色直接用后端返回的 color（文档八-5）
      return COLOR_MAP[this.health.color] || COLOR_MAP.gray;
    },
    priorityOptions() {
      // 优先级索引：0=P1，1=P2，2=P3，3=P4（文档 5.1）
      return [
        { value: 0, label: 'P1' },
        { value: 1, label: 'P2' },
        { value: 2, label: 'P3' },
        { value: 3, label: 'P4' }
      ];
    },
    columns() {
      return [
        { title: this.$t('ai_availability.ticketNumber'), slot: 'ticketNumber', minWidth: 160 },
        { title: this.$t('ai_availability.customerName'), key: 'companyName', minWidth: 160,
          render: (h, { row }) => h('span', row.companyName || '-') },
        { title: this.$t('ai_availability.priority'), slot: 'priority', minWidth: 90 },
        { title: this.$t('ai_availability.faultType'), slot: 'faultType', minWidth: 110 },
        { title: this.$t('ai_availability.stuckDuration'), slot: 'stuckDuration', minWidth: 110 },
        { title: this.$t('ai_availability.currentStatus'), slot: 'status', minWidth: 100 },
        { title: this.$t('ai_availability.faultNotify'), slot: 'notified', minWidth: 100 },
        { title: this.$t('ai_availability.alarmTime'), key: 'alarmTime', minWidth: 160,
          render: (h, { row }) => h('span', row.alarmTime || '-') },
        { title: this.$t('ai_availability.operation'), slot: 'operation', fixed: 'right', minWidth: 100 }
      ];
    }
  },
  mounted() {
    // 初始加载按手动处理（isAuto 缺省），内部会启动自动刷新定时器
    this.refreshAll();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    startAutoRefresh() {
      this.stopAutoRefresh();
      // FR-005：页面默认开启自动刷新，频率 15 秒
      this.timer = setInterval(() => {
        // 自动刷新：传入 isAuto=true，并跳过已有刷新进行中的 tick，避免堆叠
        if (this.refreshInFlight) return;
        this.refreshAll(true);
      }, REFRESH_INTERVAL);
    },
    stopAutoRefresh() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    refreshAll(isAuto) {
      this.refreshing = true;
      this.refreshInFlight = true;
      // 手动刷新（含初始加载、刷新按钮）重置 15 秒倒计时；自动刷新不重启自身
      if (isAuto !== true) {
        this.startAutoRefresh();
      }
      return Promise.all([
        this.loadOverview(),
        this.loadFaultTickets(isAuto)
      ]).finally(() => {
        this.refreshing = false;
        this.refreshInFlight = false;
      });
    },
    // 统一响应：{ code, message, data }
    unwrap(res) {
      return (res && res.data && res.data.data) || {};
    },
    loadOverview() {
      return this.$api.aiAvailability.getOverview().then(res => {
        // 业务错误码：HTTP 200 但 code !== 200，提示并保持安全默认值
        if (res && res.data && res.data.code != null && res.data.code !== 200) {
          this.$Message.error(res.data.message || this.$t('ai_availability.loadFailed'));
          this.health = { status: HEALTH_UNKNOWN, statusText: '', color: 'gray', latencyMs: null, continuousFailCount: null };
          this.recentProbes = [];
          this.metrics = { todayAlarmTotal: 0, aiProcessedCount: 0, todayTimeoutNoticeCount: 0, currentFaultCount: 0 };
          this.lastRefreshTime = this.formatNow();
          return;
        }
        var d = this.unwrap(res) || {};
        var h = d.health || {};
        this.health = {
          status: h.status || HEALTH_UNKNOWN,
          statusText: h.statusText || '',
          color: h.color || 'gray',
          latencyMs: h.latencyMs != null ? h.latencyMs : null,
          continuousFailCount: h.continuousFailCount != null ? h.continuousFailCount : null
        };
        this.recentProbes = Array.isArray(h.recentProbes) ? h.recentProbes : [];
        var m = d.metrics || {};
        this.metrics = {
          todayAlarmTotal: m.todayAlarmTotal != null ? m.todayAlarmTotal : 0,
          aiProcessedCount: m.aiProcessedCount != null ? m.aiProcessedCount : 0,
          todayTimeoutNoticeCount: m.todayTimeoutNoticeCount != null ? m.todayTimeoutNoticeCount : 0,
          currentFaultCount: m.currentFaultCount != null ? m.currentFaultCount : 0
        };
        // 最近刷新时间使用后端 refreshTime，缺省时兜底本地时间
        this.lastRefreshTime = d.refreshTime || this.formatNow();
      }).catch(() => {
        this.health = { status: HEALTH_UNKNOWN, statusText: '', color: 'gray', latencyMs: null, continuousFailCount: null };
        this.recentProbes = [];
        this.metrics = { todayAlarmTotal: 0, aiProcessedCount: 0, todayTimeoutNoticeCount: 0, currentFaultCount: 0 };
        this.lastRefreshTime = this.formatNow();
      });
    },
    buildQuery() {
      // priority 为索引，0 合法，需显式判空
      var priority = this.searchForm.priority;
      if (priority === '' || priority === undefined || priority === null) priority = null;
      return {
        companyName: this.searchForm.companyName || null,
        ticketNumber: this.searchForm.ticketNumber || null,
        priority: priority,
        alarmTimeStart: this.searchForm.alarmTimeStart || null,
        alarmTimeEnd: this.searchForm.alarmTimeEnd || null,
        pageNum: this.current,
        pageSize: this.pageSize
      };
    },
    loadFaultTickets(isAuto) {
      this.tableLoading = true;
      return this.$api.aiAvailability.getFaultTickets(this.buildQuery()).then(res => {
        // 业务错误码：HTTP 200 但 code !== 200，提示并保持安全默认值
        if (res && res.data && res.data.code != null && res.data.code !== 200) {
          this.$Message.error(res.data.message || this.$t('ai_availability.loadFailed'));
          this.faultTickets = [];
          this.total = 0;
          return;
        }
        var d = this.unwrap(res);
        // 分页结构 SLPage：{ records, total, current, size }
        var list = (d && Array.isArray(d.records)) ? d.records : [];
        this.total = (d && d.total != null) ? d.total : list.length;
        if (d && d.current != null) this.current = d.current;
        // 后端已按 P1 优先、同级卡住时长倒序排好，前端按返回顺序渲染
        if (isAuto === true) {
          // 仅在同页同筛选条件下的自动刷新时，标记新出现的故障工单用于高亮
          list.forEach(it => {
            it._isNew = this.knownTicketKeys.length > 0 && this.knownTicketKeys.indexOf(this.ticketKey(it)) === -1;
          });
        } else {
          // 手动操作（搜索/重置/翻页/手动刷新/初始加载）：不标记高亮，仅重置基线
          list.forEach(it => {
            it._isNew = false;
          });
        }
        this.knownTicketKeys = list.map(it => this.ticketKey(it));
        this.faultTickets = list;
      }).catch(() => {
        this.faultTickets = [];
        this.total = 0;
      }).finally(() => {
        this.tableLoading = false;
      });
    },
    search() {
      this.current = 1;
      this.loadFaultTickets();
    },
    resetSearch() {
      this.searchForm.companyName = '';
      this.searchForm.ticketNumber = '';
      this.searchForm.priority = '';
      this.searchForm.alarmTimeRange = ['', ''];
      this.searchForm.alarmTimeStart = '';
      this.searchForm.alarmTimeEnd = '';
      this.current = 1;
      this.loadFaultTickets();
    },
    onAlarmTimeChange(range) {
      if (Array.isArray(range)) {
        this.searchForm.alarmTimeStart = range[0] || '';
        this.searchForm.alarmTimeEnd = range[1] || '';
      } else {
        this.searchForm.alarmTimeStart = '';
        this.searchForm.alarmTimeEnd = '';
      }
    },
    changePage(p) {
      this.current = p;
      this.loadFaultTickets();
    },
    changeSize(s) {
      this.pageSize = s;
      this.current = 1;
      this.loadFaultTickets();
    },
    ticketKey(row) {
      return row.ticketNumber || '';
    },
    rowClassName(row) {
      return row._isNew ? 'fault-row-new' : '';
    },
    // ===== 健康状态展示辅助 =====
    probeColor(probe) {
      // 色块颜色直接用后端返回的 color
      return COLOR_MAP[probe.color] || COLOR_MAP.gray;
    },
    probeStatusText(probe) {
      if (probe.success === false) return this.$t('ai_availability.probeFail');
      if (probe.color === 'orange') return this.$t('ai_availability.probeSlow');
      return this.$t('ai_availability.probeSuccess');
    },
    // ===== 故障工单列表辅助 =====
    priorityColor(priorityIndex) {
      if (priorityIndex === 0) return 'red';   // P1
      if (priorityIndex === 1) return 'orange'; // P2
      if (priorityIndex === 2) return 'blue';   // P3
      return 'default';                         // P4 及其它
    },
    // 卡住时长：后端给秒数，前端格式化为可读时长（文档八-4）
    stuckDurationText(row) {
      var seconds = Number(row.stuckSeconds);
      if (!seconds || isNaN(seconds) || seconds <= 0) return '-';
      var d = Math.floor(seconds / 86400);
      var h = Math.floor((seconds % 86400) / 3600);
      var m = Math.floor((seconds % 3600) / 60);
      var parts = [];
      if (d > 0) parts.push(d + this.$t('ai_availability.dayUnit'));
      if (h > 0) parts.push(h + this.$t('ai_availability.hourUnit'));
      parts.push(m + this.$t('ai_availability.minuteUnit'));
      return parts.join('');
    },
    ticketStatusText(status) {
      var item = TICKET_STATUS[status];
      return item ? this.$t('ai_availability.' + item.textKey) : '-';
    },
    ticketStatusColor(status) {
      var item = TICKET_STATUS[status];
      return item ? item.color : 'default';
    },
    // 故障类型展示：后端返回中文（"响应超时"/"处理超时"），EN 模式下映射到 i18n
    faultTypeText(row) {
      var ft = row && row.faultType;
      if (ft === '响应超时' || ft === 'RESPONSE_TIMEOUT') return this.$t('ai_availability.responseTimeout');
      if (ft === '处理超时' || ft === 'PROCESS_TIMEOUT') return this.$t('ai_availability.processTimeout');
      return ft || '-';
    },
    toTicketDetail(row) {
      // CONF-004：跳转工单中心既有详情页（按工单 id 跳转，后端按更新后的契约在每条故障工单记录中返回 ticketId）
      if (row.ticketId == null) return;
      this.$router.push({
        path: '/cases/caseDetail',
        query: { id: row.ticketId }
      });
    },
    formatNow() {
      var d = new Date();
      var pad = n => (n < 10 ? '0' + n : '' + n);
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' +
        pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds());
    }
  }
};
</script>

<style scoped>
.ai-availability-page {
  padding-bottom: 16px;
}
/* 标题 */
.page-title {
  width: 100%;
  font-size: 14px;
  font-weight: 600;
  color: #17233d;
  box-sizing: border-box;
  padding: 14px 4px 12px 4px;
  display: flex;
  align-items: center;
}
.title-bar {
  display: inline-block;
  width: 3px;
  height: 14px;
  background-color: #2d8cf0;
  margin-right: 8px;
  border-radius: 1px;
}
.title-refresh {
  margin-left: auto;
}
/* 顶部综合面板 */
.overview-panel {
  background-color: #fff;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  margin-bottom: 12px;
}
.overview-col {
  padding: 18px 20px;
  box-sizing: border-box;
}
.health-col {
  border-right: 1px solid #e8eaec;
}
.health-main {
  display: flex;
  align-items: center;
}
.health-dot-lg {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 14px;
  flex: 0 0 40px;
}
.health-info {
  flex: 1;
}
.health-name {
  font-size: 18px;
  font-weight: 600;
  color: #17233d;
}
.health-sub {
  font-size: 12px;
  color: #808695;
  margin-top: 2px;
}
.health-metrics {
  display: flex;
  text-align: right;
}
.health-metric {
  margin-left: 24px;
}
.hm-label {
  font-size: 12px;
  color: #808695;
}
.hm-value {
  font-size: 16px;
  font-weight: 600;
  color: #17233d;
  margin-top: 2px;
}
.probe-row {
  margin-top: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.probe-label {
  font-size: 12px;
  color: #808695;
  margin-right: 10px;
}
.probe-dots {
  display: inline-flex;
  align-items: center;
}
.probe-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-right: 4px;
  cursor: pointer;
}
.probe-empty {
  color: #c5c8ce;
}
.probe-legend {
  margin-left: 16px;
}
.legend-item {
  font-size: 12px;
  color: #808695;
  margin-right: 12px;
  display: inline-flex;
  align-items: center;
}
.legend-dot {
  width: 10px;
  height: 10px;
  margin-right: 4px;
  cursor: default;
}
/* 指标列 */
.metric-col {
  border-right: 1px solid #e8eaec;
  border-top: 3px solid transparent;
}
.metric-col:last-child {
  border-right: none;
}
.metric-total { border-top-color: #2d8cf0; }
.metric-processed { border-top-color: #19be6b; }
.metric-timeout { border-top-color: #ff9900; }
.metric-fault { border-top-color: #ed4014; }
.metric-label {
  font-size: 13px;
  color: #515a6e;
}
.metric-value {
  font-size: 30px;
  font-weight: 600;
  color: #17233d;
  margin: 6px 0 4px;
  line-height: 1.1;
}
.metric-fault .metric-value { color: #ed4014; }
.metric-desc {
  font-size: 12px;
  color: #808695;
}
/* 故障工单面板 */
.fault-panel {
  background-color: #fff;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  padding: 14px 16px 16px;
}
.panel-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #17233d;
  display: inline-flex;
  align-items: center;
}
.refresh-time {
  margin-left: auto;
  font-size: 12px;
  color: #808695;
}
.search-row {
  margin-bottom: 12px;
}
.stuck-duration {
  color: #ed4014;
}
.page-bar {
  margin-top: 14px;
  text-align: right;
}
</style>
<style>
/* 新出现的故障工单高亮（非 scoped 以作用到 Table 行） */
.ivu-table .fault-row-new td {
  background-color: #fff7e6 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/cases/ai_availability/AIAvailabilityDashboard.vue